<template>
  <iRow wrap="nowrap">
    <iSpace :width="marginWidth" />
    <iRow :wrap="!$vssMobile ? 'nowrap' : 'wrap'" align="between">
      <iColumn gap="extraSmall">
        <iRow :wrap="!$vssMobile ? 'nowrap' : 'wrap'" vertical-align="middle">
          <iRow
            gap="extraSmall"
            vertical-align="middle"
            width="hug"
            wrap="nowrap"
          >
            <iText variant="subtle">
              Destination:
            </iText>
            <iColumn width="hug">
              <iButton
                v-if="story.publishing_platform_id == null"
                class="text-btn"
                variant="text"
                @click="triggerStoryAssignModal"
              >
                <iText :wrap="false">
                  Assign to platform
                </iText>
              </iButton>
              <iText v-else :wrap="false">
                {{ story.publishing_platform_name }}
              </iText>
            </iColumn>
          </iRow>
          <iIcon
            v-if="story.publishing_platform_type === PUBLISHING_PLATFORM_TYPE.PUBLICATION"
            :is-loading="updateStoryExclusivityIsLoading"
            :label="isStoryExclusive ? 'Must be exclusive' : 'Can be free'"
            :tooltip="exclusiveCheckboxTooltip"
            :variant="isStoryExclusive ? 'branded' : 'subtle'"
            icon="dollar"
            :disabled="!canEdit"
            @click="updateStoryExclusivityFlag"
          />
          <iIcon
            v-if="story.publishing_platform_type === PUBLISHING_PLATFORM_TYPE.SYNDICATION_MARKETPLACE"
            :variant="isStoryRequiresSyndicationApproval ? 'branded' : 'subtle'"
            icon="lock"
            :is-loading="updateStoryRequiresSyndicationApprovalContentIsLoading"
            label="Anyone can publish"
            tooltip="When enabled you will need to approve the syndication of this story."
            :disabled="!canEdit"
            @click="updateStoryRequiresSyndicationApprovalFlag"
          />
          <iIcon
            v-if="story.allow_remix === 1"
            variant="branded"
            icon="pencil"
            label="Allow Remix"
            disable-hover
            tooltip="When enabled you allow users to modify your story before syndicating it."
          />
          <iIcon
            v-if="canEdit"
            :icon="isPreview ? 'visibility' : 'visibility-off'"
            :variant="isPreview ? 'branded' : 'subtle'"
            :tooltip="isPreview ? 'Show edit mode' : 'Show preview mode'"
            @click="isPreview = !isPreview"
          />
          <iText
            v-if="isPublishedAndLive"
            :wrap="false"
            variant="success"
          >
            Published & Live
          </iText>

          <iText
            v-if="story.allow_remix === 1 && !isPublishedAndLive"
            :wrap="false"
            variant="branded"
          >
            Unpublished Draft
          </iText>
        </iRow>
        <iRow v-if="story.allow_remix === 1">
          <iText variant="subtle">
            Tags:
          </iText>
          <template v-if="hashtags.length">
            <iButton
              v-for="tag in hashtags"
              :key="tag.hashtag_id"
              variant="text"
              :disabled="!canEdit"
              @click="triggerHashtagsModal = true"
            >
              #{{ tag.hashtag }}
            </iButton>
          </template>
          <template v-else>
            <iButton
              class="text-btn"
              variant="text"
              :disabled="!canEdit"
              @click="triggerHashtagsModal = true"
            >
              No Tags
            </iButton>
          </template>
        </iRow>
      </iColumn>
      <iRow align="right" vertical-align="middle">
        <iText
          v-if="!isPublishedAndLive && canEdit"
          :wrap="false"
          variant="branded"
        >
          {{ savingStatus }}
        </iText>
        <iButton
          v-if="!isPublishedAndLive"
          :disabled="!canPublish"
          :is-loading="isPublishingStory"
          loading-spinner-position="before"
          :loader-on-left="true"
          size="small"
          @click="publishingStory"
        >
          Publish
        </iButton>
        <iButton
          v-else
          :is-loading="isUnpublishingStory"
          loading-spinner-position="before"
          size="small"
          variant="secondary"
          @click="unpublishStoryAction"
        >
          Unpublish
        </iButton>
      </iRow>
    </iRow>
    <iSpace :width="marginWidth" />
  </iRow>
  <iEditor
    ref="editor"
    :key="playbackVersion"
    :branding-styles="getCssMappedConfiguration"
    :include-margin="includeMargin"
    :margin-width="marginWidth"
    :preview="!canEdit || isPreview"
    :segments="resolvedSegments"
    :upload-post-image="uploadImageFunction"
    :upload-post-video="uploadVideoFunction"
    :upload-post-audio="uploadAudioFunction"
    height="fill"
    overflow="auto"
    variant="builder"
    width="fill"
    @input="saveWithDebounce"
    @handle-link-modal-open="handleLinkModalOpen"
  >
    <template #above-editor>
      <iRow
        :style-overrides="{marginTop: '8px'}"
        width="fill"
        wrap="nowrap"
      >
        <iTextEditInput
          ref="title"
          v-model="title"
          :min-width="250"
          :disabled="!canEdit"
          placeholder="Untitled Story"
          font-size="extraExtraLarge"
          font-weight="dark"
          editable-on-click
          auto-save-on="input"
          hide-borders
          inline
          @submit="saveTitleWithDebounce($event)"
        />
      </iRow>
    </template>
  </iEditor>

  <teleport to="body">
    <RichTextDestinationPickerModal
      :show="showLinkModal"
      :original-link-value="originalLinkData.linkValue"
      :link-data="linkData"
      :publication-id="story.publishing_platform_id"
      :rich-text-ref="linkData.ref"
      @close="handleLinkModalClose"
      @update="linkData = {...linkData, ...$event}"
    />
  </teleport>

  <AssignSyndicatedPublishingPlatformModal
    v-if="assignToSyndicatedPublishingPlatformModalOpen"
    :story-id="story.story_id"
    @change="assignStory"
    @close="() => { assignToSyndicatedPublishingPlatformModalOpen = false; forcePublishAfterAssigning = false }"
  />
  <AssignExclusivePublishingPlatformModal
    v-if="assignToExclusivePublishingPlatformModalOpen"
    :story-id="story.story_id"
    @change="assignStory"
    @close="() => { assignToExclusivePublishingPlatformModalOpen = false; forcePublishAfterAssigning = false }"
  />

  <template v-if="story">
    <ShowVariantsModal
      :parent-id="story.variant_parent_id"
      :show="showShowStoryVariantsModal"
      :story-id="story.story_id"
      :story-title="story.title"
      @close="showShowStoryVariantsModal = false"
    />
    <CreateVariantsModal
      :show="showCreateVariantsModal"
      :story-id="story.story_id"
      :story-title="story.title"
      @close="showCreateVariantsModal = false"
    />

    <iModal
      v-if="showEditStoryConfirmation"
      :close-on-backdrop="false"
      :close-on-escape="false"
      :close-on-secondary="false"
      :primary-action-button-loading="updateStoryIsLoading"
      :width="600"
      primary-action-button-label="Continue"
      title="Edit Story"
      @close="closeEditStoryConfirmation"
      @click:primary="forceDraftStatusConfirmation"
      @click:secondary="closeEditStoryConfirmation"
    >
      <template #body>
        <iParagraph>
          Your story is currently live. If you want to continue editing, your story will be changed to draft.
        </iParagraph>
      </template>
    </iModal>
  </template>

  <iModal
    v-if="triggerHashtagsModal"
    :close-on-backdrop="false"
    :close-on-escape="false"
    :close-on-secondary="false"
    :max-width="600"
    :secondary-action-button-loading="updateStoryIsLoading"
    :show-primary-action-button="false"
    secondary-action-button-label="Close"
    title="Hashtags"
    width="fill"
    @close="triggerHashtagsModal = false"
    @click:secondary="triggerHashtagsModal = false"
  >
    <template #body>
      <iRow>
        <iHashtagInput
          v-model="hashtags"
          :validation="validateHashtag"
          hashtag-value-key="hashtag"
          @submit:hashtag="addHashtag"
          @delete:hashtag="removeHashtag"
        />
      </iRow>
    </template>
  </iModal>
</template>

<script>
import { mapActions, mapState, mapWritableState } from "pinia";
import { usePublicationStore } from "@/stores/publication-store";
import { postStatus } from "@/enums/post-enums";
import { getRelativeTimeString, useWindowStore } from "@vcl";
import ShowVariantsModal from "@/components/publication/ShowVariantsModal";
import CreateVariantsModal from "@/components/publication/CreateVariantsModal";
import { useStoryStore } from "%/stores/story-store";
import { STORY_VARIANT_TYPES } from "@/constants/story-variant-type-constants";
import { PUBLISHING_PLATFORM_TYPE } from "%/constants/story-constants";
import AssignSyndicatedPublishingPlatformModal from "@/components/publication/AssignSyndicatedPublishingPlatformModal.vue";
import AssignExclusivePublishingPlatformModal from "@/components/publication/AssignExclusivePublishingPlatformModal.vue";
import useBrandingStore from "%/stores/branding-store";
import RichTextDestinationPickerModal from "%/components/RichTextDestinationPickerModal.vue";
import { StoryVideoPlaybackMixin } from "%/mixins/story-video-playback-mixin";

export default {
  name: "SyndicatedStoryEditorComponent",
  components: {
    RichTextDestinationPickerModal,
    AssignExclusivePublishingPlatformModal,
    AssignSyndicatedPublishingPlatformModal,
    CreateVariantsModal,
    ShowVariantsModal,
  },
  mixins: [StoryVideoPlaybackMixin],
  props: {
    storyData: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    saveWhileActive: {
      type: Boolean,
      required: false,
      default: false,
    },
    includeMargin: {
      type: Boolean,
      required: false,
      default: true,
    },
    marginWidth: {
      type: Number,
      required: false,
      default: 100,
    },
  },
  data() {
    return {
      isStoryExclusive: false,
      isStoryRequiresSyndicationApproval: false,
      showEditStoryConfirmation: false,
      debounce: null,
      storyLastUpdated: null,
      title: "",
      isPreview: false,
      triggerHashtagsModal: false,
      showShowStoryVariantsModal: false,
      showCreateVariantsModal: false,
      initialLoad: 0,
      savingStatus: "Changes Saved",
      forceDraftStatus: false,
      STORY_VARIANT_TYPES,
      PUBLISHING_PLATFORM_TYPE,
      savingStatuses: {
        SAVED: "Changes Saved",
        UNSAVED: "Unsaved Changes",
        SAVING: "...Saving Changes",
        ERROR: "Error Saving Changes",
      },
      unsavedTitle: null,
      unsavedContent: null,
      showLinkModal: false,
      linkData: {},
      originalLinkData: {},
    };
  },
  computed: {
    ...mapState(useWindowStore, [
      "$vssMobile",
    ]),
    ...mapState(usePublicationStore, [
      "publicationId",
      "myPublications",
    ]),
    ...mapState(useBrandingStore, [
      "getCssMappedConfiguration",
    ]),
    ...mapState(useStoryStore, [
      "uploadStoryImageLoading",
      "loadingAddHashtagToStory",
      "errorAddHashtagToStory",
      "updateStoryExclusivityIsLoading",
      "updateStoryRequiresSyndicationApprovalContentIsLoading",
      "isPublishingStory",
      "publishingStory",
      "isUnpublishingStory",
    ]),
    ...mapState(useStoryStore, {
      storyStoreData: "story",
    }),
    ...mapWritableState(useStoryStore, [
      "updateStoryIsLoading",
      "assignToSyndicatedPublishingPlatformModalOpen",
      "forcePublishAfterAssigning",
    ]),

    story() {
      if (
        this.storyData &&
          typeof this.storyData === "object" &&
          Object.keys(this.storyData).length === 0
      ) {
        return this.storyStoreData;
      }
      return this.storyData;
    },

    canEdit() {
      if (this.story.status === "author_rejected") {
        return false;
      }
      const isRemixAllowed = this.story.allow_remix === 1;
      const isNotPending = this.story.status !== "pending_author_approval";
      return isRemixAllowed && isNotPending;
    },

    canPublish() {
      if (this.story.status === "author_rejected") {
        return false;
      }
      return this.story.status !== "pending_author_approval";
    },

    isPublishedAndLive() {
      return (
        this.story.status === "active" &&
          this.savingStatus === "Changes Saved" &&
          !this.forceDraftStatus
      );
    },

    // Segment source the playback mixin resolves media-backed videos against.
    playbackSegments() {
      return this.segments;
    },

    segments() {
      const segmentArray = Object.values(this.story.segments || {});
      return (this.story.segment_order || []).reduce((acc, curr) => {
        const segment = segmentArray.find(
          seg => seg.story_segment_id === curr.story_segment_id,
        );
        if (segment) {
          acc.push(segment);
        }
        return acc;
      }, []);
    },

    exclusiveCheckboxTooltip() {
      return "You can mark this content as exclusive. This will make it only visible to active subscribers.";
    },

    hashtags: {
      get() {
        return this.story.hashtags || [];
      },
      set(value) {
        this.story.hashtags = value;
      },
    },
  },
  watch: {
    "story.publishing_platform_id": {
      immediate: true,
      handler(value) {
        if (this.story.publishing_platform_type === "publication") {
          window.blclient.data.links.fanWeb = window.blclient.data.links.web + `/destination/${value}`;
        }
      },
    },
  },
  mounted() {
    if (this.story.title) {
      this.title = this.story.title || "New Draft Story";
    }
    this.isStoryExclusive = !!this.story.is_exclusive;
    this.isStoryRequiresSyndicationApproval = !!this.story.requires_syndication_approval;
    useWindowStore().init();
  },
  beforeUnmount() {
    useWindowStore().remove();
  },
  methods: {
    ...mapActions(useStoryStore, [
      "uploadStoryImage",
      "uploadStoryVideo",
      "uploadStoryAudio",
      "updateStory",
      "addHashtagsToStory",
      "removeHashtagFromStory",
      "updateStoryExclusiveContentFlag",
      "updateStoryRequiresSyndicationApprovalContentFlag",
      "updateStoryTitle",
      "assignStoryToPublishingPlatform",
      "publishingStory",
      "triggerStoryAssignModal",
      "unpublishStory",
    ]),

    closeEditStoryConfirmation() {
      this.title = this.story.title;
      this.$refs.editor.editorRef.resetBuilderEditor();
      this.showEditStoryConfirmation = false;
    },

    forceDraftStatusConfirmation() {
      this.showEditStoryConfirmation = false;
      this.forceDraftStatus = true;
      if (this.unsavedContent) {
        this.saveWithDebounce(this.unsavedContent);
      }
      if (this.unsavedTitle) {
        this.saveTitleWithDebounce(this.unsavedTitle);
      }
    },

    saveWithDebounce(content) {
      this.handleBeforeSave("content", content, () => {
        if (this.debounce) {
          clearTimeout(this.debounce);
        }
        this.debounce = setTimeout(() => {
          this.savingStatus = this.savingStatuses.SAVING;
          // Keep { mediaId } canonical — never persist the display-only resolved src.
          this.submitStory(
            { ...content, segments: this.stripResolvedVideoSrc(content.segments || []) },
            () => {
              this.setSavingStatusToSaved();
              this.unsavedContent = null;
            },
            () => {
              this.savingStatus = this.savingStatuses.ERROR;
            },
          );
        }, 1000);
      });
    },

    saveTitleWithDebounce(newTitle) {
      if (newTitle === this.title) {
        return;
      }
      this.handleBeforeSave("title", newTitle, () => {
        this.title = newTitle;
        this.updateStoryIsLoading = true;
        if (this.debounce) {
          clearTimeout(this.debounce);
        }
        this.debounce = setTimeout(() => {
          this.savingStatus = this.savingStatuses.SAVING;
          this.updateStoryTitle(
            this.story.story_id,
            this.title,
            () => {
              this.setSavingStatusToSaved();
              this.unsavedTitle = null;
            },
            () => {
              this.savingStatus = this.savingStatuses.ERROR;
            },
          );
        }, 1000);
      });
    },

    addHashtag(newTag) {
      this.savingStatus = this.savingStatuses.SAVING;
      this.addHashtagsToStory(
        [newTag],
        this.story.story_id,
        () => {
          this.savingStatus = this.savingStatuses.ERROR;
          this.hashtags.pop();
        },
        () => {
          this.setSavingStatusToSaved();
        },
      );
    },

    handleBeforeSave(context, dataForContext, process = () => {}) {
      this.initialLoad++;
      if (
        context !== "title" &&
          this.initialLoad <= this.segments.length - 1 &&
          this.segments.length > 1
      ) {
        return;
      }
      if (
        this.story.status === postStatus.ACTIVE &&
          !this.saveWhileActive &&
          !this.forceDraftStatus
      ) {
        if (context === "content") {
          this.unsavedContent = dataForContext;
        } else if (context === "title") {
          this.unsavedTitle = dataForContext;
        }
        this.showEditStoryConfirmation = true;
        return;
      }
      this.savingStatus = this.savingStatuses.UNSAVED;
      process();
    },

    removeHashtag({ el, index }) {
      this.savingStatus = this.savingStatuses.SAVING;
      this.removeHashtagFromStory(
        el.hashtag_id,
        this.story.story_id,
        () => {
          this.savingStatus = this.savingStatuses.ERROR;
          this.hashtags.splice(index, 0, el);
        },
        () => {
          this.setSavingStatusToSaved();
        },
      );
    },

    validateHashtag(value) {
      const result = /^(#)?[a-zA-Z0-9_]+$/.test(value);
      if (!result) {
        return "Hashtag must be alphanumeric and can only contain underscores.";
      }
      return true;
    },

    setSavingStatusToSaved() {
      setTimeout(() => {
        this.savingStatus = this.savingStatuses.SAVED;
      }, 200);
    },

    async uploadImageFunction(file) {
      return await this.uploadStoryImage(file);
    },

    async uploadVideoFunction(file) {
      return await this.uploadStoryVideo(file);
    },
    async uploadAudioFunction(file) {
      return await this.uploadStoryAudio(file);
    },

    submitStory(content, onComplete = () => {}, onError = () => {}) {
      if (this.story.status !== postStatus.ACTIVE) {
        this.updateStoryIsLoading = false;
      }
      this.updateStory(this.story.story_id, content, onComplete, onError);
    },

    updateStoryExclusivityFlag() {
      this.savingStatus = this.savingStatuses.SAVING;
      this.updateStoryExclusiveContentFlag(
        this.story.story_id,
        !this.isStoryExclusive,
        () => {
          this.setSavingStatusToSaved();
          this.isStoryExclusive = !this.isStoryExclusive;
        },
        () => {
          this.savingStatus = this.savingStatuses.ERROR;
        },
      );
    },

    updateStoryRequiresSyndicationApprovalFlag() {
      this.savingStatus = this.savingStatuses.SAVING;
      this.updateStoryRequiresSyndicationApprovalContentFlag(
        this.story.story_id,
        !this.isStoryRequiresSyndicationApproval,
        () => {
          this.setSavingStatusToSaved();
          this.isStoryRequiresSyndicationApproval =
                !this.isStoryRequiresSyndicationApproval;
        },
        () => {
          this.savingStatus = this.savingStatuses.ERROR;
        },
      );
    },

    assignStory(details) {
      this.savingStatus = this.savingStatuses.SAVING;
      this.assignToSyndicatedPublishingPlatformModalOpen = false;

      this.assignStoryToPublishingPlatform(
        this.story.story_id,
        details.publishing_platform_type,
        details.publishing_platform_id,
        () => {
          this.setSavingStatusToSaved();
          this.story.publishing_platform_id = details.publishing_platform_id;
          this.story.publishing_platform_type = details.publishing_platform_type;
          this.story.publishing_platform_name = details.publishing_platform_name;
          if (this.forcePublishAfterAssigning) {
            this.publishingStory();
            this.forcePublishAfterAssigning = false;
          }
        },
        () => {
          this.savingStatus = this.savingStatuses.ERROR;
        },
      );
    },

    async unpublishStoryAction() {
      this.unpublishStory(this.story.story_id, true);
    },

    handleLinkModalOpen(event) {
      if (this.story.publishing_platform_type === "publication") {
        this.linkData = event;
        this.originalLinkData = event;
        this.showLinkModal = true;
        return;
      }
      event.openModal(true);
    },
    handleLinkModalClose() {
      this.linkData = {};
      this.originalLinkData = {};
      this.showLinkModal = false;
    },

    getRelativeTimeString,
  },
};
</script>

<style lang="scss" scoped>
:deep(toptap.ProseMirror) {
  height: 100%;
  box-sizing: border-box;
  cursor: text;
}

.native-story-title {
  margin-top: 4px;

  :deep(.i-text.text-edit) {
    width: 100%;
    font-size: 26px;
    font-weight: 700;
  }
}

.text-btn {
  opacity: 0.5;
}
</style>
